import { useEffect, useState } from 'react';
import { trpClient } from '../client/trpClient';
import { CriteriaPointer, CriteriaPointersResult } from '../types/criteriaPointers';

/**
 * Robbie's pointers for the RFQ's "Additional notes".
 *
 * `loading` while the server reads them (the first read takes a few seconds),
 * `ready` with the pointers, `empty` when there are no notes, `unavailable`
 * when there are notes but no pointers: the model failed, found nothing, or
 * the call itself failed. Every non-ready phase is quiet — the caller shows
 * the buyer's own notes instead of an error.
 *
 * One read per RFQ and notes text, shared by every surface that shows the
 * criteria (checkpoint, reveal card, Smart BOM sheet); edited notes are a new
 * key, so they are read afresh.
 */
export type CriteriaPointersPhase = 'loading' | 'ready' | 'empty' | 'unavailable';

export interface CriteriaPointersState {
  phase: CriteriaPointersPhase;
  pointers: CriteriaPointer[];
}

const EMPTY: CriteriaPointersState = { phase: 'empty', pointers: [] };
const UNAVAILABLE: CriteriaPointersState = { phase: 'unavailable', pointers: [] };
const LOADING: CriteriaPointersState = { phase: 'loading', pointers: [] };

const toState = (result: CriteriaPointersResult): CriteriaPointersState => {
  if (result.status === 'EMPTY') return EMPTY;
  if (result.status === 'READY' && result.pointers.length > 0) return { phase: 'ready', pointers: result.pointers };
  return UNAVAILABLE;
};

const reads = new Map<string, Promise<CriteriaPointersState>>();
// Settled reads, so a surface opened after the answer arrived starts on it
// instead of flashing the loading chip for one frame.
const settled = new Map<string, CriteriaPointersState>();

const readKey = (rfqId: string, notes: string) => `${rfqId}\n${notes.trim()}`;

const readPointers = (rfqId: string, key: string): Promise<CriteriaPointersState> => {
  let read = reads.get(key);
  if (!read) {
    read = trpClient.getCriteriaPointers(rfqId).then(toState);
    read.then(
      (state) => settled.set(key, state),
      // A failed read is not remembered, so the next surface to open retries.
      () => reads.delete(key),
    );
    reads.set(key, read);
  }
  return read;
};

const initialState = (rfqId: string | undefined, notes: string): CriteriaPointersState => {
  if (!rfqId || !notes.trim()) return EMPTY;
  return settled.get(readKey(rfqId, notes)) || LOADING;
};

export const useCriteriaPointers = (rfqId: string | undefined, notes: string): CriteriaPointersState => {
  const [state, setState] = useState<CriteriaPointersState>(() => initialState(rfqId, notes));

  useEffect(() => {
    setState(initialState(rfqId, notes));
    if (!rfqId || !notes.trim()) return undefined;
    let current = true;
    readPointers(rfqId, readKey(rfqId, notes)).then(
      (next) => current && setState(next),
      () => current && setState(UNAVAILABLE),
    );
    return () => {
      current = false;
    };
  }, [rfqId, notes]);

  return state;
};

/** Test seam: forget every read. */
export const resetCriteriaPointerReads = (): void => {
  reads.clear();
  settled.clear();
};

export default useCriteriaPointers;
